import { ALL_ERROR_CODES } from '@diora/contracts';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { splitByLocale, type StringTree } from './pair';
import { strings } from './strings';

export type Locale = 'ar' | 'en';
export const LOCALES: readonly Locale[] = ['ar', 'en'];

/**
 * Arabic leads.
 *
 * It is the API's fallback locale, and defaulting the dashboard to English
 * would mean the one language the server falls back to is the one the staff UI
 * treats as secondary.
 */
export const DEFAULT_LOCALE: Locale = 'ar';

const STORAGE_KEY = 'diora.locale';

export function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'ar' || stored === 'en') return stored;
  } catch {
    // Private mode, or storage blocked. The default stands.
  }
  return DEFAULT_LOCALE;
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not being able to remember the choice is not a reason to refuse it.
  }
}

/** The pair tree, flattened to one language, is what i18next reads. */
const resources = {
  ar: { translation: splitByLocale(strings as unknown as StringTree, 'ar') },
  en: { translation: splitByLocale(strings as unknown as StringTree, 'en') },
};

void i18next.use(initReactI18next).init({
  resources,
  lng: readStoredLocale(),
  fallbackLng: DEFAULT_LOCALE,
  defaultNS: 'translation',
  interpolation: {
    // React escapes for us; escaping twice turns an apostrophe into &#39;.
    escapeValue: false,
  },
  returnNull: false,
});

/**
 * Every error code the API can raise must have words here.
 *
 * Checked in development only, and loudly: a code with no translation reaches a
 * person as SHOUTING_SNAKE_CASE, which is a bug that hides well in a language
 * the developer does not read.
 */
if (import.meta.env.DEV) {
  const translated = new Set(Object.keys(strings.errors));
  const missing = ALL_ERROR_CODES.filter((code) => !translated.has(code));
  if (missing.length > 0) {
    console.error(
      `[i18n] ${missing.length} API error code(s) have no translation: ${missing.join(', ')}`,
    );
  }

  // And the reverse — a translation for a code the API no longer raises is
  // dead weight that will quietly outlive the feature it described.
  const known = new Set<string>(ALL_ERROR_CODES);
  const stale = Object.keys(strings.errors).filter((code) => !known.has(code));
  if (stale.length > 0) {
    console.warn(`[i18n] ${stale.length} translated code(s) the API no longer raises: ${stale.join(', ')}`);
  }
}

export function applyDocumentLocale(locale: Locale): void {
  const root = document.documentElement;
  root.lang = locale;
  // `dir` belongs on the document, derived from the locale — never on a wrapper
  // div, and never toggled per component.
  root.dir = locale === 'ar' ? 'rtl' : 'ltr';
}

export { i18next };

/**
 * Key checking is done by `scripts/check-translations.mjs`, not by the type
 * system.
 *
 * i18next can type `t()` against the resource tree, which builds a union of
 * every dotted path in it. At this string count that type took `tsc` past a
 * gigabyte of memory and several minutes without finishing — a typecheck nobody
 * will run is worth less than one that does. The script verifies the same
 * thing (every key passed to `t()` exists; every error code has words) in
 * milliseconds, and it runs in CI where a type error would only have run
 * locally.
 *
 * The guarantee that actually matters is still enforced by the compiler: the
 * `satisfies Record<string, Pair>` on the string tree means an English value
 * without its Arabic twin does not build.
 */
